import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler } from '../PlayerHandler';

interface PlayerSubscriptionsProps {
  userId: string;
}

interface SubscriptionItem {
  id?: string;
  productName?: string;
  startDate?: string;
  endDate?: string;
  autoRenewal?: boolean;
  isActive?: boolean;
}

const PlayerSubscriptions: React.FC<PlayerSubscriptionsProps> = ({ userId }) => {
  const { theme, isMobile } = useTheme();
  const [subs, setSubs] = useState<SubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [extendModal, setExtendModal] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extendReason, setExtendReason] = useState('');

  const fetchSubs = async () => {
    try {
      const handler = new PlayerHandler();
      const response = await handler.getPlayerSubscriptions(userId);
      if (response?.success) {
        setSubs(response.subscriptions || []);
      }
    } catch (err) {
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubs(); }, [userId]);

  const handleExtend = async (subId: string) => {
    setActionLoading(subId);
    try {
      const handler = new PlayerHandler();
      await handler.extendSubscription(subId, extendDays, extendReason);
      setExtendModal(null);
      setExtendDays(30);
      setExtendReason('');
      await fetchSubs();
    } catch (err) {
      console.error('Error extending subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (subId: string) => {
    if (!confirm('Cancel this subscription?')) return;
    setActionLoading(subId);
    try {
      const handler = new PlayerHandler();
      await handler.cancelSubscription(subId, 'Admin cancelled');
      await fetchSubs();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '12px' },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: `1px solid ${theme.border}`,
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    name: { fontSize: '14px', fontWeight: 600, color: theme.text, minWidth: '120px' },
    dates: { fontSize: '12px', color: theme.textSecondary },
    badge: {
      fontSize: '11px',
      padding: '2px 8px',
      borderRadius: '10px',
      fontWeight: 600,
    },
    actions: { display: 'flex', gap: '6px' },
    actionBtn: {
      padding: '4px 10px',
      fontSize: '11px',
      fontWeight: 500,
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'transparent',
      color: theme.text,
    },
    cancelBtn: {
      padding: '4px 10px',
      fontSize: '11px',
      fontWeight: 500,
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '6px',
      cursor: 'pointer',
      background: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
    },
    empty: { fontSize: '13px', color: theme.textSecondary, fontStyle: 'italic' as const },
    modalOverlay: {
      position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      background: theme.card, borderRadius: '12px', padding: '24px',
      width: '360px', maxWidth: '90vw',
    },
    input: {
      width: '100%', padding: '8px 12px', fontSize: '14px',
      border: `1px solid ${theme.border}`, borderRadius: '8px',
      background: theme.inputBg || theme.tableHeader, color: theme.text,
      outline: 'none', marginBottom: '12px', boxSizing: 'border-box' as const,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Subscriptions</div>
      {loading && <div style={styles.empty}>Loading...</div>}
      {!loading && subs.length === 0 && <div style={styles.empty}>No subscriptions</div>}
      {subs.map((s) => (
        <div key={s.id} style={styles.row}>
          <span style={styles.name}>{s.productName}</span>
          <span style={styles.dates}>{formatDate(s.startDate)} - {formatDate(s.endDate)}</span>
          <span style={{
            ...styles.badge,
            background: s.isActive ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: s.isActive ? '#22c55e' : '#ef4444',
          }}>
            {s.isActive ? 'Active' : 'Expired'}
            {s.autoRenewal && ' (Auto)'}
          </span>
          <div style={styles.actions}>
            <button style={styles.actionBtn} onClick={() => setExtendModal(s.id!)} disabled={actionLoading === s.id}>
              Extend
            </button>
            {s.isActive && (
              <button style={styles.cancelBtn} onClick={() => handleCancel(s.id!)} disabled={actionLoading === s.id}>
                Cancel
              </button>
            )}
          </div>
        </div>
      ))}

      {extendModal && (
        <div style={styles.modalOverlay} onClick={() => setExtendModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: theme.text, marginBottom: '16px' }}>Extend Subscription</div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: theme.text, display: 'block', marginBottom: '4px' }}>Days to extend</label>
            <input style={styles.input} type="number" min={1} value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value, 10) || 1)} />
            <label style={{ fontSize: '13px', fontWeight: 600, color: theme.text, display: 'block', marginBottom: '4px' }}>Reason</label>
            <input style={styles.input} value={extendReason} onChange={(e) => setExtendReason(e.target.value)} placeholder="Reason" />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={styles.actionBtn} onClick={() => setExtendModal(null)}>Cancel</button>
              <button style={{ ...styles.actionBtn, background: '#3b82f6', color: '#fff', border: 'none' }} onClick={() => handleExtend(extendModal)}>
                Extend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerSubscriptions;
